import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withRouter } from 'app/helpers/routing';
import { bindAll } from '@bibliocommons/utils-react';
import {
  libraryShape,
  didNotFindOptionsShape,
  paginationShape,
  routerShape,
  userIdentityShape
} from '@bibliocommons/bc-prop-types';

import {
  setSearchOrigin,
  trackAnalyticsEvent,
  trackCatalogExplore,
  trackCatalogSearch,
  trackContentClick,
  trackContentExplore,
  trackContentImpression,
  trackEventInterested,
  trackHoldingsConsumeAccess,
  trackHoldingsView
} from 'app/actions/AnalyticsGa4Actions';
import CardsActions from 'app/actions/CardsActions';
import DrawerActions from 'app/actions/DrawerActions';
import EventsActions from 'app/actions/EventsActions';
import ListPresentationActions from 'app/actions/ListPresentationActions';
import SearchActions from 'app/actions/SearchActions';
import SearchAnalyticsContext from 'app/components/search/SearchAnalyticsContext';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { SEARCH_ORIGIN_QUERY_PARAM } from 'app/constants/AnalyticsConstants';
import buildCatalogSearchPayload, { buildSearchEntity } from 'app/helpers/analytics/buildCatalogSearchPayload';
import {
  eventNames,
  METADATA_CATEGORY_TYPE,
  PROPERTY_CONTENT_COLLECTION_TYPE,
  UI_COMPONENT_TYPE,
  UI_CONTAINER_TITLE,
  UI_CONTAINER_TYPE
} from 'app/constants/AnalyticsGa4Constants';
import { VIEW } from 'app/constants/SearchConstants';
import mapPromoRecommendationTypeToProperty from 'app/helpers/analytics/mapPromoRecommendationTypeToProperty';
import { mapDisplayBibToAnalyticsBibEntity } from 'app/helpers/displayBib';
import hasSingleBranchStatus from 'app/helpers/search/hasSingleBranchStatus';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { selectSearchResultBib } from 'app/selectors/SearchSelector';
import { selectCurrentSiteScopes } from 'app/selectors/ScopeSelector';
import { selectCardsForSearch } from 'app/selectors/CardsSelector';
import EmptySearchResultContainer from 'app/components/search/EmptySearchResult/EmptySearchResultContainer';
import GroupSearchFeedbackContainer from 'app/components/GroupSearchFeedback/GroupSearchFeedbackContainer';
import cardShape from 'app/shapes/cardShape';
import {
  activeFiltersShape,
  branchShape,
  catalogSearchOptionsShape,
  drawerShape,
  fieldsShape,
  searchResultShape,
  searchTypeShape,
  shelfNameShape,
  sortBysShape
} from 'app/shapes/search';
import MobileSearchBox from '../MobileSearchBox';
import SearchTitle from '../SearchTitle';
import ShelfAvailabilityTitle from '../ShelfAvailabilityTitle';

import Search from './Search';

export class SearchContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  componentDidMount() {
    const { router, searchActions } = this.props;

    const origin = router.query[SEARCH_ORIGIN_QUERY_PARAM] ?? null;

    if (origin) {
      searchActions.setShouldPreventImmediateRefetch(true);
      this.removeOriginFromQuery();
    }

    this.trackSearch();
  }

  componentDidUpdate(prevProps) {
    const { isFetching, router, setSearchOrigin: dispatchSetSearchOrigin, searchActions } = this.props;

    const { isFetching: wasFetching, router: prevRouter } = prevProps;

    const origin = router.query?.[SEARCH_ORIGIN_QUERY_PARAM] ?? null;
    const prevOrigin = prevRouter.query?.[SEARCH_ORIGIN_QUERY_PARAM] ?? null;

    if (origin && origin !== prevOrigin) {
      this.removeOriginFromQuery();
    } else if (prevOrigin) {
      dispatchSetSearchOrigin({ searchOrigin: prevOrigin });
    } else {
      searchActions.setShouldPreventImmediateRefetch(false);
    }

    if (wasFetching && !isFetching) {
      this.trackSearch();
    }

    this.resetPageOnViewChange(prevProps);
  }

  componentWillUnmount() {
    const { setSearchOrigin: dispatchSetSearchOrigin } = this.props;

    dispatchSetSearchOrigin({ searchOrigin: null });
  }

  getAnalyticsSearchEntity() {
    const { query, router, searchOrigin, searchType } = this.props;

    return buildSearchEntity({ query, searchType, searchOrigin, router });
  }

  removeOriginFromQuery() {
    const { router } = this.props;

    const newQuery = { ...router.query };

    delete newQuery[SEARCH_ORIGIN_QUERY_PARAM];

    router.replace({
      query: newQuery
    });
  }

  renderMobileSearchBox() {
    const { activeFilters, isMobileApp, locked, orderedSearchTypes, query, searchType } = this.props;
    if (!isMobileApp) {
      return null;
    }

    return (
      <MobileSearchBox
        locked={locked}
        searchTypes={orderedSearchTypes}
        query={query}
        searchType={searchType}
        activeFilters={activeFilters}
      />
    );
  }

  renderHeading({ withDidYouMean } = { withDidYouMean: true }) {
    const { currentUser, shelfName } = this.props;
    if (currentUser && shelfName) {
      return <ShelfAvailabilityTitle shelfName={shelfName} userId={currentUser.get('id')} />;
    }

    const {
      broadened,
      broadenedSearchType,
      customEdit,
      didNotFindOptions,
      didYouMeanQuerySuggestion,
      didYouMeanResultCount,
      query,
      router,
      rssURL,
      searchType,
      showExternalSearchLink,
      suppress,
      titleKey
    } = this.props;

    return (
      <SearchTitle
        broadened={broadened}
        broadenedSearchType={broadenedSearchType}
        customEdit={customEdit}
        didNotFindOptions={didNotFindOptions}
        didYouMeanQuerySuggestion={didYouMeanQuerySuggestion}
        didYouMeanResultCount={didYouMeanResultCount}
        id="searchTitle"
        query={query}
        router={router}
        rssURL={rssURL}
        searchType={searchType}
        showDidYouMean={withDidYouMean}
        showExternalSearchLink={showExternalSearchLink}
        suppress={suppress}
        titleKey={titleKey}
      />
    );
  }

  resetPageOnViewChange(prevProps) {
    const { router } = this.props;

    if (this.props.currentView !== prevProps.currentView) {
      router.replace({
        query: {
          ...router.query,
          page: undefined
        }
      });
    }
  }

  trackAddToListClick({ displayAvailability, displayBib, uiEntity }) {
    const { trackAnalyticsEvent: dispatchAnalyticsEvent } = this.props;

    const bibEntity = mapDisplayBibToAnalyticsBibEntity(displayBib, displayAvailability);

    dispatchAnalyticsEvent({
      entities: {
        bib: bibEntity,
        ui: uiEntity
      },
      eventName: eventNames.CONTENT_COLLECTION_ADD,
      properties: {
        content_collection_type: PROPERTY_CONTENT_COLLECTION_TYPE.LIST_TITLE
      }
    });
  }

  trackAddToNewListClick({ displayAvailability, displayBib, linkText }) {
    const { trackAnalyticsEvent: dispatchAnalyticsEvent } = this.props;

    const bibEntity = mapDisplayBibToAnalyticsBibEntity(displayBib, displayAvailability);

    dispatchAnalyticsEvent({
      entities: {
        bib: bibEntity,
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT
        }
      },
      eventName: eventNames.USER_CONTENT_INITIATE
    });
  }

  trackBibAuthorClick({ bibId, author, itemPosition, itemPositionGrouped, totalCount }) {
    const { currentView, pagination, sortBys, trackCatalogExplore: dispatchTrackCatalogExplore } = this.props;

    dispatchTrackCatalogExplore({
      bibId,
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_CREATORS,
        metadata_value: author
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
        ui_component_label: author,
        ui_container_type: UI_CONTAINER_TYPE.ITEM,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count'),
        ui_content_position: itemPosition,
        ui_content_position_grouped: itemPositionGrouped,
        ui_content_position_grouped_count: totalCount
      }
    });
  }

  trackBibJacketClick({ bib, itemPosition, itemPositionGrouped, totalCount }) {
    const { currentView, pagination, sortBys, trackContentClick: dispatchTrackContentClick } = this.props;

    const briefInfo = bib.get('briefInfo');

    dispatchTrackContentClick({
      bibId: bib.get('id'),
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
        metadata_value: briefInfo.get('title')
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
        ui_container_type: UI_CONTAINER_TYPE.ITEM,
        ui_content_layout: currentView ?? 'medium',
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count'),
        ui_content_position: itemPosition,
        ui_content_position_grouped: itemPositionGrouped,
        ui_content_position_grouped_count: totalCount
      }
    });
  }

  trackBibTitleClick({ bib, itemPosition, itemPositionGrouped, totalCount }) {
    const { currentView, pagination, sortBys, trackContentClick: dispatchTrackContentClick } = this.props;

    dispatchTrackContentClick({
      bibId: bib.get('id'),
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
        metadata_value: bib.get('title')
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_label: bib.get('title'),
        ui_component_type: UI_COMPONENT_TYPE.LINK_HEADING,
        ui_container_type: UI_CONTAINER_TYPE.ITEM,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count'),
        ui_content_position: itemPosition,
        ui_content_position_grouped: itemPositionGrouped,
        ui_content_position_grouped_count: totalCount
      }
    });
  }

  trackCardExploreClick({ card, linkText, position, promo, promoSearchRequestId }) {
    const { currentView, pagination, sortBys, trackContentExplore: dispatchTrackContentExplore } = this.props;

    const cardId = card?.get('id');
    const slug = card?.getIn(['contentType', 'slug'])?.replace('-', '_');
    const recommendationType = promo?.get('recommendationType');

    dispatchTrackContentExplore({
      cardId,
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.CONTENT_TYPE,
        metadata_value: slug
      },
      properties: {
        impression_id: `${cardId}-${promoSearchRequestId}`,
        promotion_type: mapPromoRecommendationTypeToProperty(recommendationType)
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_label: linkText,
        ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
        ui_container_position: position,
        ui_container_title: UI_CONTAINER_TITLE.PERSONALIZED_PROMOS,
        ui_container_type: UI_CONTAINER_TYPE.CARD,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count')
      }
    });
  }

  trackCardImageClick({ card, position, promo, promoSearchRequestId }) {
    const { currentView, pagination, sortBys, trackContentClick: dispatchTrackContentClick } = this.props;

    const cardId = card?.get('id');
    const recommendationType = promo?.get('recommendationType');

    dispatchTrackContentClick({
      cardId,
      properties: {
        impression_id: `${cardId}-${promoSearchRequestId}`,
        promotion_type: mapPromoRecommendationTypeToProperty(recommendationType)
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
        ui_container_position: position,
        ui_container_title: UI_CONTAINER_TITLE.PERSONALIZED_PROMOS,
        ui_container_type: UI_CONTAINER_TYPE.ITEM,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count')
      }
    });
  }

  trackCardImpression({ card, position, promo, promoSearchRequestId }) {
    const { currentView, pagination, sortBys, trackContentImpression: dispatchTrackContentImpression } = this.props;

    const cardId = card?.get('id');
    const recommendationType = promo?.get('recommendationType');

    dispatchTrackContentImpression({
      cardId,
      properties: {
        impression_id: `${cardId}-${promoSearchRequestId}`,
        promotion_type: mapPromoRecommendationTypeToProperty(recommendationType)
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_container_position: position,
        ui_container_title: UI_CONTAINER_TITLE.PERSONALIZED_PROMOS,
        ui_container_type: UI_CONTAINER_TYPE.CARD,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count')
      }
    });
  }

  trackCardTitleClick({ card, position, promo, promoSearchRequestId }) {
    const { currentView, pagination, sortBys, trackContentClick: dispatchTrackContentClick } = this.props;

    const cardId = card?.get('id');
    const recommendationType = promo?.get('recommendationType');

    dispatchTrackContentClick({
      cardId,
      properties: {
        impression_id: `${cardId}-${promoSearchRequestId}`,
        promotion_type: mapPromoRecommendationTypeToProperty(recommendationType)
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_type: UI_COMPONENT_TYPE.LINK_HEADING,
        ui_container_position: position,
        ui_container_title: UI_CONTAINER_TITLE.PERSONALIZED_PROMOS,
        ui_container_type: UI_CONTAINER_TYPE.CARD,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count')
      }
    });
  }

  trackRecommendationClick({ bib, recommendationType }) {
    const { currentView, pagination, sortBys, trackContentClick: dispatchTrackContentClick } = this.props;

    dispatchTrackContentClick({
      bibId: bib.id,
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
        metadata_enrichment_provider: recommendationType?.toLowerCase(),
        metadata_value: bib.title
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
        ui_container_title: UI_CONTAINER_TITLE.RECOMMENDATION_WHILE_YOU_WAIT,
        ui_container_type: UI_CONTAINER_TYPE.CAROUSEL,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count')
      }
    });
  }

  trackSearch() {
    const {
      currentView,
      pagination,
      query,
      router,
      searchOrigin,
      searchType,
      sortBys,
      trackCatalogSearch: dispatchTrackCatalogSearch
    } = this.props;

    dispatchTrackCatalogSearch(
      buildCatalogSearchPayload({ query, searchType, searchOrigin, router, currentView, pagination, sortBys })
    );
  }

  trackViewAvailabilityClick({ bib, linkText, itemPosition, itemPositionGrouped, totalCount }) {
    const { currentView, pagination, sortBys, trackHoldingsView: dispatchTrackHoldingsView } = this.props;

    const bibId = bib.get('id');

    dispatchTrackHoldingsView({
      bibId,
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_label: linkText,
        ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
        ui_container_type: UI_CONTAINER_TYPE.ITEM,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count'),
        ui_content_position: itemPosition,
        ui_content_position_grouped: itemPositionGrouped,
        ui_content_position_grouped_count: totalCount
      }
    });
  }

  trackViewDigitalResourceClick({ bib, eresourceDescription, eresourceUrl }) {
    const {
      currentView,
      pagination,
      sortBys,
      trackHoldingsConsumeAccess: dispatchTrackHoldingsConsumeAccess
    } = this.props;

    dispatchTrackHoldingsConsumeAccess({
      bibId: bib.get('id'),
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.HOLDINGS_ELECTRONIC_RESOURCE,
        metadata_value: eresourceUrl
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
        ui_component_label: eresourceDescription,
        ui_container_type: UI_CONTAINER_TYPE.ITEM,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count')
      }
    });
  }

  trackViewManifestationClick({ bib, linkText, itemPosition, itemPositionGrouped, totalCount }) {
    const { currentView, pagination, sortBys, trackContentClick: dispatchTrackContentClick } = this.props;

    dispatchTrackContentClick({
      bibId: bib.get('id'),
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
        metadata_value: bib.getIn(['briefInfo', 'title'])
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_component_label: linkText,
        ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
        ui_container_type: UI_CONTAINER_TYPE.ITEM,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count'),
        ui_content_position: itemPosition,
        ui_content_position_grouped: itemPositionGrouped,
        ui_content_position_grouped_count: totalCount
      }
    });
  }

  trackCardEventInterestedClick({ card, position, promo, promoSearchRequestId }) {
    const { currentView, pagination, sortBys, trackEventInterested: dispatchTrackEventInterested } = this.props;

    const cardId = card?.get('id');
    const recommendationType = promo?.get('recommendationType');

    dispatchTrackEventInterested({
      cardId,
      properties: {
        impression_id: `${cardId}-${promoSearchRequestId}`,
        promotion_type: mapPromoRecommendationTypeToProperty(recommendationType)
      },
      search: this.getAnalyticsSearchEntity(),
      ui: {
        ui_container_position: position,
        ui_container_title: UI_CONTAINER_TITLE.PERSONALIZED_PROMOS,
        ui_container_type: UI_CONTAINER_TYPE.CARD,
        ui_content_layout: currentView,
        ui_content_page: pagination.get('page'),
        ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
        ui_content_total_count: pagination.get('count')
      }
    });
  }

  renderContent() {
    const {
      activeFilters,
      branches,
      catalogSearchOptions,
      currentView,
      drawer,
      featuredContentBlockPosition,
      fields,
      isFetching,
      pagination,
      query,
      resultPaneActive,
      results,
      rssURL,
      searchType,
      singleBranch,
      sortBys,
      zeroResults,
      didNotFindOptions,
      featuredCards
    } = this.props;

    // Actions
    const { drawerActions, listPresentationActions, searchActions, cardsActions, eventsActions } = this.props;

    if (zeroResults) {
      return (
        <EmptySearchResultContainer
          renderHeading={this.renderHeading}
          renderMobileSearchBox={this.renderMobileSearchBox}
        />
      );
    }

    return (
      <div>
        <SearchAnalyticsContext.Provider
          value={{
            trackAddToListClick: this.trackAddToListClick,
            trackAddToNewListClick: this.trackAddToNewListClick,
            trackBibAuthorClick: this.trackBibAuthorClick,
            trackBibJacketClick: this.trackBibJacketClick,
            trackBibTitleClick: this.trackBibTitleClick,
            trackCardExploreClick: this.trackCardExploreClick,
            trackCardImageClick: this.trackCardImageClick,
            trackCardImpression: this.trackCardImpression,
            trackCardTitleClick: this.trackCardTitleClick,
            trackRecommendationClick: this.trackRecommendationClick,
            trackViewAvailabilityClick: this.trackViewAvailabilityClick,
            trackViewDigitalResourceClick: this.trackViewDigitalResourceClick,
            trackViewManifestationClick: this.trackViewManifestationClick,
            trackCardEventInterestedClick: this.trackCardEventInterestedClick
          }}
        >
          <GroupSearchFeedbackContainer searchResults={results} />
          <Search
            activeFilters={activeFilters}
            branches={branches}
            catalogSearchOptions={catalogSearchOptions}
            currentUser={this.props.currentUser}
            currentView={currentView}
            drawer={drawer}
            drawerActions={drawerActions}
            featuredContentBlockPosition={featuredContentBlockPosition}
            fields={fields}
            isFetching={isFetching}
            library={this.props.library}
            libraryShortName={this.props.libraryShortName}
            listPresentationActions={listPresentationActions}
            locked={this.props.locked}
            pagination={pagination}
            query={query}
            renderHeading={this.renderHeading}
            renderMobileSearchBox={this.renderMobileSearchBox}
            resultPaneActive={resultPaneActive}
            results={results}
            rssURL={rssURL}
            searchActions={searchActions}
            searchType={searchType}
            singleBranch={singleBranch}
            sortBys={sortBys}
            didNotFindOptions={didNotFindOptions}
            featuredCards={featuredCards}
            cardsActions={cardsActions}
            eventsActions={eventsActions}
          />
        </SearchAnalyticsContext.Provider>
      </div>
    );
  }

  render() {
    const { intl } = this.props;

    return (
      <div className="container">
        <DocumentHeadContainer pageTitle={intl.formatMessage({ id: 'searchTitle' })} />
        {this.renderContent()}
      </div>
    );
  }
}

SearchContainer.propTypes = {
  activeFilters: activeFiltersShape.isRequired,
  branches: ImmutablePropTypes.listOf(branchShape).isRequired,
  broadened: PropTypes.bool,
  broadenedSearchType: PropTypes.string,
  catalogSearchOptions: catalogSearchOptionsShape.isRequired,
  currentUser: userIdentityShape,
  currentView: PropTypes.oneOf(Object.values(VIEW)).isRequired,
  customEdit: PropTypes.bool,
  didYouMeanQuerySuggestion: PropTypes.string,
  didYouMeanResultCount: PropTypes.number,
  drawer: drawerShape.isRequired,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  featuredContentBlockPosition: PropTypes.number,
  fields: fieldsShape.isRequired,
  isFetching: PropTypes.bool,
  isMobileApp: PropTypes.bool.isRequired,
  library: libraryShape,
  libraryShortName: PropTypes.string.isRequired,
  listPresentationActions: PropTypes.objectOf(PropTypes.func).isRequired,
  locked: PropTypes.bool,
  pagination: paginationShape.isRequired,
  query: PropTypes.string,
  resultPaneActive: PropTypes.bool,
  results: ImmutablePropTypes.listOf(searchResultShape).isRequired,
  rssURL: PropTypes.string,
  featuredCards: ImmutablePropTypes.listOf(cardShape).isRequired,
  searchActions: PropTypes.objectOf(PropTypes.func).isRequired,
  cardsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  eventsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  searchType: searchTypeShape.isRequired,
  shelfName: shelfNameShape,
  showExternalSearchLink: PropTypes.bool,
  singleBranch: PropTypes.bool,
  sortBys: sortBysShape.isRequired,
  suppress: PropTypes.bool,
  titleKey: PropTypes.string,
  zeroResults: PropTypes.bool,
  didNotFindOptions: didNotFindOptionsShape,
  orderedSearchTypes: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  router: routerShape.isRequired,
  searchOrigin: PropTypes.string,
  setSearchOrigin: PropTypes.func.isRequired,
  trackAnalyticsEvent: PropTypes.func.isRequired,
  trackCatalogExplore: PropTypes.func.isRequired,
  trackCatalogSearch: PropTypes.func.isRequired,
  trackContentClick: PropTypes.func.isRequired,
  trackContentExplore: PropTypes.func.isRequired,
  trackContentImpression: PropTypes.func.isRequired,
  trackEventInterested: PropTypes.func.isRequired,
  trackHoldingsConsumeAccess: PropTypes.func.isRequired,
  trackHoldingsView: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const catalogSearch = state.getIn(['search', 'catalogSearch']);
  const library = selectCurrentLibrary(state);

  const currentScopeId = state.getIn(['search', 'catalogSearch', 'scope']);
  const libraryScopes = selectCurrentSiteScopes(state);
  const isLibrarySingleBranch = library.get('singleBranch');
  const isLibraryScopeFacetEnabled = library.get('scopeFacetEnabled');

  const isSingleBranch = hasSingleBranchStatus({
    currentScopeId,
    isLibrarySingleBranch,
    isLibraryScopeFacetEnabled,
    libraryScopes
  });

  return {
    activeFilters: catalogSearch.get('activeFilters'),
    branches: library.get('branches'),
    broadened: catalogSearch.get('broadened'),
    broadenedSearchType: catalogSearch.get('broadenedSearchType'),
    catalogSearchOptions: state.getIn(['search', 'catalogSearchOptions']),
    currentUser: selectCurrentUser(state),
    currentView: catalogSearch.get('view'),
    customEdit: catalogSearch.get('custom_edit'),
    didYouMeanQuerySuggestion: catalogSearch.get('didYouMeanQuerySuggestion'),
    didYouMeanResultCount: catalogSearch.get('didYouMeanResultCount'),
    drawer: state.get('drawer'),
    featuredContentBlockPosition: library.get('featuredContentBlockPosition'),
    fields: catalogSearch.get('fields'),
    isFetching: catalogSearch.get('isFetching'),
    isMobileApp: state.getIn(['app', 'mobileApp'], false),
    libraryShortName: library.get('shortName'),
    orderedSearchTypes: library.get('orderedSearchTypes', Immutable.List()),
    locked: catalogSearch.get('locked'),
    pagination: catalogSearch.get('pagination'),
    query: catalogSearch.get('query', ''),
    resultPaneActive: catalogSearch.get('resultPaneActive'),
    results: selectSearchResultBib(state),
    featuredCards: selectCardsForSearch(state),
    rssURL: catalogSearch.get('rssURL'),
    searchOrigin: state.getIn(['analyticsGa4', 'searchOrigin']),
    searchType: catalogSearch.get('searchType'),
    shelfName: catalogSearch.get('shelfName'),
    showExternalSearchLink: library.get('showExternalSearchLink'),
    singleBranch: isSingleBranch,
    sortBys: catalogSearch.get('sortBys'),
    suppress: catalogSearch.get('suppress'),
    titleKey: catalogSearch.get('title_key'),
    zeroResults: catalogSearch.get('zeroResults'),
    didNotFindOptions: library.get('didNotFindOptions'),
    library
  };
};

const mapDispatchToProps = dispatch => ({
  searchActions: bindActionCreators(SearchActions, dispatch),
  cardsActions: bindActionCreators(CardsActions, dispatch),
  drawerActions: bindActionCreators(DrawerActions, dispatch),
  eventsActions: bindActionCreators(EventsActions, dispatch),
  listPresentationActions: bindActionCreators(ListPresentationActions, dispatch),
  setSearchOrigin: bindActionCreators(setSearchOrigin, dispatch),
  trackAnalyticsEvent: bindActionCreators(trackAnalyticsEvent, dispatch),
  trackCatalogExplore: bindActionCreators(trackCatalogExplore, dispatch),
  trackCatalogSearch: bindActionCreators(trackCatalogSearch, dispatch),
  trackContentClick: bindActionCreators(trackContentClick, dispatch),
  trackContentExplore: bindActionCreators(trackContentExplore, dispatch),
  trackContentImpression: bindActionCreators(trackContentImpression, dispatch),
  trackEventInterested: bindActionCreators(trackEventInterested, dispatch),
  trackHoldingsConsumeAccess: bindActionCreators(trackHoldingsConsumeAccess, dispatch),
  trackHoldingsView: bindActionCreators(trackHoldingsView, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(SearchContainer)));
